import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const API_URL = process.env.REACT_APP_API_URL;

const GuestCheckout = () => {
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", phoneNumber: "", email: "", address: "",
    city: "", state: "", country: "", pincode: "", landmark: ""
  });
  const [cart, setCart] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [error, setError] = useState("");
  const [isAddressSaved, setIsAddressSaved] = useState(false);

  useEffect(() => {
    const guestCart = JSON.parse(localStorage.getItem("guest_cart")) || [];
    const total = guestCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setCart(guestCart);
    setTotalPrice(total);

    const savedAddress = JSON.parse(localStorage.getItem("guestAddress"));
    if (savedAddress) {
      setFormData(savedAddress);
      setIsAddressSaved(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = value;

    if (["firstName", "lastName", "city", "state", "country"].includes(name)) {
      updated = updated.replace(/[^a-zA-Z\s]/g, "");
    }
    if (name === "phoneNumber") {
      updated = updated.replace(/\D/g, "").slice(0, 10);
    }
    if (name === "pincode") {
      updated = updated.replace(/\D/g, "").slice(0, 6);
    }

    setFormData({ ...formData, [name]: updated });
  };

  const validateAddress = () => {
    const required = ["firstName", "lastName", "phoneNumber", "email", "address", "city", "state", "country", "pincode"];
    for (let field of required) {
      if (!formData[field]) {
        setError(`Please fill in ${field}`);
        return false;
      }
    }
    if (!/^\d{10}$/.test(formData.phoneNumber)) {
      setError("Phone number must be 10 digits.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      setError("Invalid email format.");
      return false;
    }
    if (!/^\d{6}$/.test(formData.pincode)) {
      setError("Pincode must be 6 digits.");
      return false;
    }

    setError("");
    return true;
  };

  const handleSaveAddress = () => {
    if (!validateAddress()) return;
    localStorage.setItem("guestAddress", JSON.stringify(formData));
    setIsAddressSaved(true);
    alert("Address saved successfully.");
  };

  const handleEdit = () => {
    setIsAddressSaved(false);
  };

  const handlePayment = async () => {
    if (!formData || !formData.email) return setError("No address or email found.");
    if (cart.length === 0) return setError("Cart is empty.");

    try {
      const stripe = await stripePromise;

      // Prepare payload with the correct structure that matches the backend DTO
      const payload = {
        email: formData.email,
        totalAmount: totalPrice,
        address: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          pincode: formData.pincode,
          landmark: formData.landmark || ""
        },
        products: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
        })),
      };

      const { data } = await axios.post(`${API_URL}/api/guest-orders/create`, payload);

      const result = await stripe.redirectToCheckout({ sessionId: data.id });

      if (result.error) {
        console.error(result.error.message);
        setError("Stripe redirection failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Payment failed. Please try again.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Guest Checkout</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}

      {!isAddressSaved ? (
        <form className="grid grid-cols-1 gap-4">
          {Object.keys(formData).map((field) => (
            field === "address" ? (
              <textarea key={field} name={field} value={formData[field]} onChange={handleChange} placeholder={field} className="border p-2 rounded" />
            ) : (
              <input key={field} name={field} value={formData[field]} onChange={handleChange} placeholder={field} className="border p-2 rounded" />
            )
          ))}
          <button type="button" onClick={handleSaveAddress} className="bg-green-600 text-white p-2 rounded">
            Save Address
          </button>
        </form>
      ) : (
        <div className="border p-4 rounded bg-green-50 mb-4">
          <p><strong>{formData.firstName} {formData.lastName}</strong></p>
          <p>{formData.address}, {formData.city}, {formData.state}, {formData.country} - {formData.pincode}</p>
          <p>Phone: {formData.phoneNumber} | Email: {formData.email}</p>
          <p>Landmark: {formData.landmark}</p>
          <button onClick={handleEdit} className="bg-yellow-500 text-white px-3 py-1 mt-2 rounded">Edit</button>
        </div>
      )}

      <div className="mt-6">
        <h3 className="text-xl mb-2">Cart Summary</h3>
        <ul className="mb-4">
          {cart.map((item, i) => (
            <li key={i}>{item.title} x {item.quantity} - ₹{item.price * item.quantity}</li>
          ))}
        </ul>
        <p className="font-bold text-lg mb-4">Total: ₹{totalPrice}</p>
        <button
          onClick={handlePayment}
          className={`bg-indigo-600 text-white p-3 w-full rounded-lg ${!isAddressSaved ? "opacity-50 cursor-not-allowed" : ""}`}
          disabled={!isAddressSaved}
        >
          Proceed to Pay ₹{totalPrice}
        </button>
      </div>
    </div>
  );
};

export default GuestCheckout;